import { LineBatchMeta } from '@shared/components/ui/LineBatchMeta';
import { Product } from '@shared/components/ui/Product';
import { Badge } from '@shared/components/ui/shadcn/badge';
import { ScrollArea } from '@shared/components/ui/shadcn/scroll-area';
import { CheckCircle2, Clock, MapPin, Package, User, XCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EntryReceiptMovement } from '../../../models/Entry';

interface ReceivedMovementsListProps {
  movements: EntryReceiptMovement[];
  isLoading?: boolean;
  showTitle?: boolean;
}

const buildImageUrl = (imageUrl?: string | null): string => {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('http')) return imageUrl;
  return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageUrl}`;
};

export const ReceivedMovementsList: React.FC<ReceivedMovementsListProps> = ({
  movements,
  isLoading = false,
  showTitle = true,
}) => {
  const { t } = useTranslation('merchandiseV3');
  const [, setCurrentTime] = useState(new Date());

  useEffect(() => {
    // Actualizar cada minuto para refrescar las fechas relativas
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // 60 segundos

    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm text-muted-foreground">{t('receive.movements.loading', 'Cargando movimientos...')}</p>
        </div>
      </div>
    );
  }

  if (movements.length === 0) {
    return (
      <div className="flex items-center justify-center h-full bg-muted/30 rounded-xl border border-dashed border-border">
        <div className="text-center p-8">
          <Package className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            {t('receive.movements.empty.title', 'No hay movimientos registrados')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('receive.movements.empty.description', 'Los movimientos aparecerán aquí')}
          </p>
        </div>
      </div>
    );
  }

  // Función auxiliar para formatear fecha de forma segura
  const safeFormatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
      // Convertir formato "2026-01-16 12:40:51" a formato ISO
      const isoDateString = dateString.replace(' ', 'T');
      const date = new Date(isoDateString);
      if (isNaN(date.getTime())) return dateString; // Si no es válida, devolver el string original

      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  };

  // Ordenar por fecha de creación (más recientes primero)
  const sortedMovements = [...movements].sort((a, b) => {
    if (!a.created_at) return 1;
    if (!b.created_at) return -1;
    try {
      const dateA = new Date(a.created_at.replace(' ', 'T'));
      const dateB = new Date(b.created_at.replace(' ', 'T'));
      if (isNaN(dateA.getTime())) return 1;
      if (isNaN(dateB.getTime())) return -1;
      return dateB.getTime() - dateA.getTime();
    } catch {
      return 0;
    }
  });

  return (
    <div className="h-full flex flex-col">
      {showTitle && (
        <h3 className="text-lg font-semibold text-foreground mb-4">
          {t('receive.movements.title', 'Movimientos Registrados')}
        </h3>
      )}
      {/* Lista ordenada cronológicamente */}
      <ScrollArea className="flex-1">
        <div className="space-y-3 pr-4">
          {sortedMovements.map((movement) => {
            const isAccepted = movement.movement_type === 'ACCEPTED';
            const productName = movement.product?.name || `Producto ${movement.product_id}`;
            const productCode = movement.product?.code || '';
            const productImage = movement.product?.img || null;
            const locationCode = movement.location?.code || `#${movement.location_id}`;
            const userName = movement.user?.name || t('receive.movements.unknown_user', 'Usuario desconocido');

            return (
              <div
                key={movement.id}
                className={`border rounded-lg p-3 transition-colors ${
                  isAccepted
                    ? 'bg-background border-border hover:border-green-300 dark:hover:border-green-700'
                    : 'bg-background border-border hover:border-red-300 dark:hover:border-red-700'
                }`}
              >
                <div className="flex items-center gap-3">
                  {/* Producto usando componente del sistema */}
                  <div className="flex-1 min-w-0">
                    <Product
                      id={movement.product_id}
                      name={productName}
                      src={buildImageUrl(productImage)}
                      code={productCode}
                      showImageModal={true}
                      enableLineClamp={true}
                    />
                  </div>

                  {/* Estado y cantidad */}
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {isAccepted ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    )}
                    <span className="text-sm font-semibold text-foreground">
                      {movement.qty} {t('receive.movements.units', 'ud.')}
                    </span>
                  </div>
                </div>

                {isAccepted && (movement.batch?.batch_number || movement.batch_id != null) && (
                  <LineBatchMeta
                    batch={movement.batch}
                    batchId={movement.batch_id}
                    i18nNs="merchandiseV3"
                    lotLabelKey="receive.movements.line_batch_label"
                    expiryLabelKey="receive.movements.line_expiry_label"
                    className="mt-2"
                  />
                )}

                {/* Información adicional en formato horizontal */}
                <div className="flex items-center justify-between text-xs text-muted-foreground mt-2 pt-2 border-t border-border">
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* Ubicación (solo para aceptados) */}
                    {isAccepted && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {locationCode}
                      </span>
                    )}

                    {/* Usuario */}
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {userName}
                    </span>

                    {/* Fecha */}
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {safeFormatDate(movement.created_at)}
                    </span>

                    {/* Motivo de rechazo */}
                    {!isAccepted && movement.rejection_type && (
                      <Badge variant="destructive" className="text-xs">
                        {movement.rejection_type}
                      </Badge>
                    )}

                    {/* Estado de despacho (solo para aceptados) */}
                    {isAccepted && (
                      <span className="flex items-center gap-1">
                        {movement.dispatch_status === 'DISPATCHED' ? (
                          <>
                            <MapPin className="w-3 h-3 text-green-600 dark:text-green-400" />
                            <span className="text-green-600 dark:text-green-400 font-medium">
                              {t('receive.movements.dispatched', 'Despachado')}
                            </span>
                          </>
                        ) : (
                          <>
                            <Clock className="w-3 h-3 text-orange-600 dark:text-orange-400" />
                            <span className="text-orange-600 dark:text-orange-400 font-medium">
                              {t('receive.movements.pending', 'Pendiente')}
                            </span>
                          </>
                        )}
                      </span>
                    )}
                  </div>
                </div>

                {/* Texto de rechazo si existe */}
                {!isAccepted && movement.rejection_text && (
                  <div className="mt-2 text-xs text-muted-foreground italic">{movement.rejection_text}</div>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};
